import { Input, DatePicker } from 'antd'
import {
  Control,
  Controller,
  FieldValues,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form'
import {
  ASSIGN_STATUS_OPTIONS,
  GRADING_STATUS_OPTIONS,
  SEARCH_FORM_FIELDS,
} from 'src/pages/test-management/config'
import { ShareSelectInput } from '@components'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo } from 'react'
import { RootState, getListTeacher, useAppDispatch } from '@redux'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import useRole from 'src/hooks/useRole'
import { Button } from 'src/common'

const { RangePicker } = DatePicker

interface ITestTableToolbarProps {
  control: Control<FieldValues, any>
  reset: UseFormReset<FieldValues>
  setValue: UseFormSetValue<FieldValues>
}

export default function TestTableToolbar({
  control,
  reset,
  setValue,
}: ITestTableToolbarProps) {
  const { t } = useTranslation(['common', 'testManagement'])
  const dispatch = useAppDispatch()
  const { data: listTeacherData } = useSelector(
    (state: RootState) => state.testManagement?.listTeacher
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const teacherIdParam = searchParams.get('teacherId') || ''

  const { isAdminRole } = useRole()

  const listTeacherOptions = useMemo(() => {
    if (
      !Array.isArray(listTeacherData?.items) ||
      !listTeacherData?.items?.length
    )
      return []

    return listTeacherData?.items.map((teacher: any) => ({
      value: teacher?.id,
      label: teacher?.name,
    }))
  }, [listTeacherData?.items])

  useEffect(() => {
    dispatch(getListTeacher())
  }, [dispatch])

  useEffect(() => {
    if (!teacherIdParam) return

    setValue(SEARCH_FORM_FIELDS.ASSIGNED_TO, Number(teacherIdParam))
  }, [listTeacherOptions, setValue, teacherIdParam])

  return (
    <div className="flex flex-wrap item-centers justify-between gap-4">
      <Controller
        name={SEARCH_FORM_FIELDS.SEARCH}
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <Input
              prefix={<SearchOutlined className="pr-2 pl-0" />}
              placeholder={t('testManagement:searchTextPlaceholder')}
              name={SEARCH_FORM_FIELDS.SEARCH}
              onChange={onChange}
              value={value}
              style={{
                maxWidth: 380,
                width: '100%',
                borderRadius: 6,
                borderColor: '#e2e8f0',
              }}
            />
          )
        }}
      />

      <div className="flex item-centers justify-between gap-4">
        <Controller
          name={SEARCH_FORM_FIELDS.DATE_RANGE}
          control={control}
          render={({ field: { value } }) => {
            return (
              <RangePicker
                value={value}
                format={'DD-MM-YYYY'}
                allowEmpty={[true, true]}
                onChange={(values) => {
                  setValue(SEARCH_FORM_FIELDS.DATE_RANGE, values)
                }}
                style={{
                  minWidth: 230,
                  borderRadius: 6,
                  borderColor: '#e2e8f0',
                }}
              />
            )
          }}
        />

        {isAdminRole ? (
          <Controller
            name={SEARCH_FORM_FIELDS.ASSIGNED_TO}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <ShareSelectInput
                  data={listTeacherOptions}
                  onChange={(value: string) => {
                    setSearchParams('')
                    onChange(value)
                  }}
                  errors={error?.message}
                  value={value}
                  style={{ marginRight: 16, minWidth: 180, maxWidth: 200 }}
                  placeholder={t('testManagement:assignedTo')}
                  mode="multiple"
                  showArrow
                />
              )
            }}
          />
        ) : null}

        {isAdminRole ? (
          <Controller
            name={SEARCH_FORM_FIELDS.ASSIGN_STATUS}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <ShareSelectInput
                  data={
                    ASSIGN_STATUS_OPTIONS as { label: string; value: string }[]
                  }
                  onChange={(value: string) => {
                    onChange(value)
                  }}
                  errors={error?.message}
                  value={value}
                  placeholder={t('testManagement:assignStatus')}
                  showArrow
                />
              )
            }}
          />
        ) : null}

        <Controller
          name={SEARCH_FORM_FIELDS.GRADING_STATUS}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <ShareSelectInput
                data={
                  GRADING_STATUS_OPTIONS as { label: string; value: string }[]
                }
                onChange={(value: string) => {
                  onChange(value)
                }}
                errors={error?.message}
                value={value}
                style={{ minWidth: 200, marginRight: 16 }}
                placeholder={t('testManagement:gradingStatus')}
                mode="multiple"
                showArrow
              />
            )
          }}
        />

        <Button
          type="primary"
          onClick={() => {
            setSearchParams('')
            reset()
          }}
          danger
          style={{
            backgroundColor: '#ff0000',
            border: 'none',
          }}
        >
          {t('common:clear')}
        </Button>
      </div>
    </div>
  )
}
